import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Search } from "lucide-react";
import { matchesSearch } from "@/components/t4r/learners/useLearnerList";

export const AddLearnersDialog = ({ open, onClose, course, onAdded }) => {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPicked(new Set()); setQ(""); api.get("/lms/admin/learners/overview").then(({ data }) => setAll(data.learners || [])); } }, [open]);
  const candidates = useMemo(() => all.filter((l) => !l.course_ids.includes(course.id) && l.status !== "archived" && matchesSearch(l, q)), [all, q, course.id]);
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/lms/admin/learners/bulk", { learner_ids: [...picked], action: "enrol", course_id: course.id });
      toast.success(`${data.updated} learner${data.updated === 1 ? "" : "s"} enrolled`); onAdded(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="add-learners-dialog">
        <DialogHeader><DialogTitle>Add existing learners</DialogTitle><DialogDescription className="text-xs">Active learners get an enrolment email. Invited learners see the course once they sign in.</DialogDescription></DialogHeader>
        <div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search learners" className="pl-8" data-testid="add-learners-search" /></div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{candidates.length} not on this course</span>
          <button className="text-green-700 hover:underline" onClick={() => setPicked(new Set(candidates.map((c) => c.id)))} data-testid="add-learners-select-all">Select all shown</button>
        </div>
        <div className="max-h-[320px] divide-y divide-slate-50 overflow-y-auto rounded-md border border-border">
          {candidates.map((l) => (
            <label key={l.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-green-50">
              <input type="checkbox" checked={picked.has(l.id)} onChange={() => toggle(l.id)} data-testid={`add-learner-${l.id}`} />
              <span className="flex-1 truncate"><span className="font-medium">{l.name}</span> <span className="text-xs text-muted-foreground">{l.email}</span></span>
            </label>
          ))}
          {candidates.length === 0 && <p className="p-4 text-xs italic text-muted-foreground">Everyone is already on this course.</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-green-700 text-white hover:bg-green-800" disabled={!picked.size || busy} onClick={add} data-testid="add-learners-confirm">Enrol {picked.size || ""}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
